import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { Network, Image as ImageIcon, GitMerge, Link2, Search } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue'
import { CategoryBadge, SourceBadges } from '../components/ConceptBadges'
import { KnowledgeFrame } from '../components/KnowledgeNav'
import { WeightLabel } from '../components/ArtworkTile'
import { categoryColor } from '../components/categoryStyle'
import { useConcepts, useKnowledgeStats } from '../hooks/useKnowledge'
import type { ConceptSort, ConceptSource } from '../api/knowledgeApi'

const PAGE_SIZE = 50
const ALL = '__all'

const SORTS: Array<{ value: ConceptSort; label: string }> = [
  { value: 'artworks', label: 'Most artworks' },
  { value: 'degree', label: 'Most connected' },
  { value: 'members', label: 'Most merged names' },
  { value: 'specificity', label: 'Least specific' },
  { value: 'name', label: 'Name' },
]

export function ConceptsPage() {
  const navigate = useNavigate()
  const { data: stats, isLoading: statsLoading } = useKnowledgeStats()
  const [q, setQ] = useState('')
  const [category, setCategory] = useState<string>(ALL)
  const [source, setSource] = useState<string>(ALL)
  const [sort, setSort] = useState<ConceptSort>('artworks')
  const [offset, setOffset] = useState(0)
  const query = useDebouncedValue(q, 250)

  const { data, isLoading, isFetching } = useConcepts({
    q: query || undefined,
    category: category === ALL ? undefined : category,
    source: source === ALL ? undefined : (source as ConceptSource),
    sort,
    limit: PAGE_SIZE,
    offset,
  })

  const reset =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v)
      setOffset(0)
    }

  const cards = [
    { label: 'Concepts', value: stats?.concepts, icon: Network },
    { label: 'Artworks described', value: stats?.artworks, icon: ImageIcon },
    { label: 'Concepts merged from several names', value: stats?.mergedConcepts, icon: GitMerge },
    { label: 'Concept-to-concept links', value: stats?.edges, icon: Link2 },
  ]
  const total = data?.total ?? 0

  return (
    <KnowledgeFrame
      title="Concept catalogue"
      description={
        <>
          Search concepts, compare their names and sources, and inspect the artworks and
          relationships they describe.
          {stats?.run && (
            <>
              {' '}
              Last build synced{' '}
              {formatDistanceToNow(new Date(stats.run.syncedAt), { addSuffix: true })}.
            </>
          )}
        </>
      }
    >
      {!statsLoading && !stats?.run && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-amber-900">No graph build synced yet</CardTitle>
            <CardDescription className="text-amber-800">
              The nightly sync copies Picasso&apos;s graph build into this database at 04:30 UTC.
              Nothing has been synced to this environment so far.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map(({ label, value, icon: Icon }) => (
          <Card key={label}>
            <CardContent className="flex items-center gap-3 p-4">
              <Icon className="h-5 w-5 text-slate-400" />
              <div>
                <div className="text-xl font-semibold tabular-nums">
                  {statsLoading ? <Skeleton className="h-6 w-16" /> : (value ?? 0).toLocaleString()}
                </div>
                <div className="text-xs text-slate-500">{label}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {stats && stats.categories.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {stats.categories.map((c) => (
            <button
              key={c.category}
              onClick={() => reset(setCategory)(category === c.category ? ALL : c.category)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
                category === c.category ? 'bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'
              }`}
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ background: categoryColor(c.category) }}
              />
              {c.category}
              <span className="tabular-nums opacity-60">{c.n.toLocaleString()}</span>
            </button>
          ))}
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[16rem] flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                className="pl-8"
                placeholder="Search concepts and every name merged into them…"
                value={q}
                onChange={(e) => reset(setQ)(e.target.value)}
              />
            </div>
            <Select value={source} onValueChange={reset(setSource)}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All sources</SelectItem>
                <SelectItem value="legacy_2024">2024 graph</SelectItem>
                <SelectItem value="enrichment_v2">Enrichment v2</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={(v) => reset(setSort)(v as ConceptSort)}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORTS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className={isFetching ? 'opacity-70' : undefined}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Concept</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Artworks</TableHead>
                <TableHead className="text-right">Links</TableHead>
                <TableHead className="text-right">Names</TableHead>
                <TableHead
                  className="text-right"
                  title="How much it counts in search: rarity, or the curator's setting"
                >
                  Weight
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading &&
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={7}>
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  </TableRow>
                ))}
              {data?.data.map((c) => (
                <TableRow
                  key={c.ucid}
                  className="cursor-pointer"
                  onClick={() => navigate(`/app/knowledge/${c.ucid}`)}
                >
                  <TableCell>
                    <div className="font-medium">{c.name}</div>
                    {c.memberNames.length > 1 && (
                      <div className="max-w-xl truncate text-xs text-slate-500">
                        also:{' '}
                        {c.memberNames
                          .filter((m) => m !== c.name)
                          .slice(0, 4)
                          .join(', ')}
                        {c.memberNames.length > 5 && ` +${c.memberNames.length - 5}`}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <CategoryBadge category={c.category} />
                  </TableCell>
                  <TableCell>
                    <SourceBadges sources={c.sources} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {c.artworkCount.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{c.degree}</TableCell>
                  <TableCell className="text-right tabular-nums">{c.memberNames.length}</TableCell>
                  <TableCell className="text-right">
                    <WeightLabel weight={c.weight} curated={c.curatorWeight != null} />
                  </TableCell>
                </TableRow>
              ))}
              {!isLoading && data?.data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-sm text-slate-500">
                    No concepts match.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {total > PAGE_SIZE && (
            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <span>
                {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total.toLocaleString()}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={offset + PAGE_SIZE >= total}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </KnowledgeFrame>
  )
}
